import { APP_ROUTES } from '@zorro/common-utils';
import { EmptyState } from '@zorro/design-system/v2/EmptyState';
import {
  AllowanceModelPendingIllustration,
  BenefitsSetupInProgressIllustration,
  NoEmployeesIllustration,
} from '@zorro/shared/assets';
import { Center } from '@zorro/web/design-system';
import { useDynamicRouter, useRoles } from '@zorro/web/ui-utils';
import { ReactNode } from 'react';

import { EmployeeActionsOverlays } from '../EmployeeActions';

const EmptyStateContainer = ({ children }: { children: ReactNode }) => (
  <Center h="calc(100vh - 18rem)">{children}</Center>
);

type AllowanceModelPendingEmptyStateProps = {
  employerId: string;
};

export const AllowanceModelPendingEmptyState = ({
  employerId,
}: AllowanceModelPendingEmptyStateProps) => {
  const { isZorroOperations, isAccountSupervisor, isAgent } = useRoles();
  const { router } = useDynamicRouter({});

  const emptyState =
    isZorroOperations || isAccountSupervisor ? (
      <EmptyState
        image={<AllowanceModelPendingIllustration />}
        title="Add the allowance model to get started"
        subtitle="Employees can be added once this plan year has an allowance model"
        mainAction={{
          label: 'Go to plan year setup',
          onClick: () =>
            void router.push(
              APP_ROUTES.OPS_COMPANY_SETUP_PLAN_YEAR_SETUP(employerId)
            ),
        }}
      />
    ) : isAgent ? (
      <EmptyState
        image={<AllowanceModelPendingIllustration />}
        title="Allowance model pending"
        subtitle="To add employees, contact Zorro team to upload an allowance model"
      />
    ) : (
      <EmptyState
        image={<BenefitsSetupInProgressIllustration />}
        title="Your benefits setup is in progress"
        subtitle="Employees can be added once your Zorro CSM uploads an allowance model"
      />
    );

  return <EmptyStateContainer>{emptyState}</EmptyStateContainer>;
};

type NoEmployeesEmptyStateProps = {
  setOverlayId: (overlayId: EmployeeActionsOverlays) => void;
};

export const NoEmployeesEmptyState = ({
  setOverlayId,
}: NoEmployeesEmptyStateProps) => (
  <EmptyStateContainer>
    <EmptyState
      image={<NoEmployeesIllustration />}
      title="No employees found"
      subtitle="Add employees to get started"
      secondaryAction={{
        label: 'Add a new employee',
        onClick: () => setOverlayId(EmployeeActionsOverlays.ADD_EMPLOYEE),
      }}
      mainAction={{
        label: 'Upload roster',
        onClick: () => setOverlayId(EmployeeActionsOverlays.UPLOAD_ROSTER),
      }}
    />
  </EmptyStateContainer>
);
